/**
 * AlphaSense Search Tool Schema
 *
 * Supports batch queries for parallel execution.
 * All queries in a single call execute concurrently on the backend.
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════════════════════
// QUERY ITEM SCHEMA
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Single query item schema
 */
export const QueryItemSchema = z.object({
    query: z.string()
        .min(1, "Query cannot be empty")
        .describe("The research question to ask AlphaSense (e.g., 'What is Apple's latest revenue?')"),
    use_findata_agent: z.boolean()
        .optional()
        .default(false)
        .describe("Set to true for queries requiring structured financial data (revenue, EPS, margins, etc.)"),
});
// ═══════════════════════════════════════════════════════════════════════════
// MAIN PARAMS SCHEMA (BATCH)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Main params schema - supports array of queries for parallel batch execution.
 *
 * Example:
 * {
 *   "queries": [
 *     { "query": "Apple Q4 revenue", "use_findata_agent": true },
 *     { "query": "Microsoft Azure growth", "use_findata_agent": false }
 *   ]
 * }
 */
export const AlphaSenseSearchParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    queries: z.array(QueryItemSchema)
        .min(1, "At least one query is required")
        .max(10, "Maximum 10 queries per batch")
        .describe("Array of queries to execute in parallel. All queries run concurrently - 3 queries take the same time as 1!"),
});
export { AlphaSenseSearchParamsSchema as SearchAlphaSenseParamsSchema };
// ═══════════════════════════════════════════════════════════════════════════
// LEGACY SINGLE-QUERY SCHEMA (backward compatibility)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Legacy single-query schema for backward compatibility.
 * Will be converted to batch format internally.
 */
export const AlphaSenseSearchParamsLegacySchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    query: z.string()
        .min(1, "Query cannot be empty")
        .describe("The research question to ask AlphaSense"),
    use_findata_agent: z.boolean()
        .optional()
        .default(false)
        .describe("Set to true for queries requiring structured financial data"),
});
export { AlphaSenseSearchParamsLegacySchema as SearchAlphaSenseLegacyParamsSchema };
